/* eslint-disable react/jsx-no-bind */
//@flow
import * as React from 'react';
import * as Immutable from 'immutable';
import { themr, type ThemeType } from 'react-css-themr';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { translate, type TFunction } from 'react-i18next';
import compose from 'recompose/compose';

import PersonnelActions from 'common/redux/actions/personnelMaster';
import ToastActions from 'common/redux/actions/toast';
import { dashboardColors } from 'common/enums/ColorEnum';
import AppPreferencesActions from 'common/redux/actions/appPreferences';

import {
    clientSelector as clientIdSelector,
    isAddEntityActive as isAddEntityActiveSelector,
    isImportEntityActive as isImportEntityActiveSelector,
    isExportEntityActive as isExportEntityActiveSelector,
    editEntityIdSelector
} from 'common/redux/selectors/queryParamsSelectors';
import {
    getEntitySettings,
    clientSelector
} from 'common/redux/selectors/clientSelector';
import { getEntityTablePreferences } from 'common/redux/selectors/appPreferences';
import PersonnelMasterSelectors from 'common/redux/selectors/personnelMaster';
import { isGlobalPersonnelSelector } from 'common/redux/selectors/sessionSelector';
import { teamsSelector } from 'common/redux/selectors/teamSelector';
import { RoleOperationEnum } from 'common/modules/rbac';

import { Checkbox } from 'common/components/core/checkbox';
import MasterExportContainer from 'common/entity/generic/MasterExportContainer';
import { withRouter } from 'common/components/core/router';
import GeneralFilter from 'common/entity/components/GeneralFilter';
import ConfirmationDialog from 'common/components/core/dialog/ConfirmationDialog';

import { entityMasterFactory } from 'common/entity/generic/MasterContainer';
import MasterContainerHOC from 'common/entity/generic/MasterContainerHOC';
import SinglePersonnelEditContainer from 'common/entity/personnelMaster/SinglePersonnelEditContainer';
import PersonnelMasterImportContainer from 'common/entity/personnelMaster/PersonnelMasterImportContainer';
import VisitUtils from 'common/utils/VisitUtils';
import {
    CellDataTypes,
    FROZEN_COLUMN_LIMIT
} from 'common/components/core/table';

import {
    ConnectedTeamSelectorFilter,
    ConnectedTeamIdSelectorFilter
} from 'common/entity/components/TeamSelectorFilter';
import { FilterBuilder, FilterOperation } from 'common/models/internal/Filter';
import TeamList from 'common/components/core/common/TeamList';
import EnablePersonnelModal from 'common/entity/personnelMaster/EnablePersonnelModal';
import FuelTypeFilter from 'common/entity/components/FuelTypeFilter';
import { AccessTypeEnum } from 'common/models/internal/MasterPersonnel';
import { IncludeTypeEnums } from 'common/enums/EntityMasterEnum';
import { PersonnelTypeEnum } from 'common/enums/PersonnelEnums';

import type { EntityColumnType } from 'common/entity/generic/MasterContainer';
import type { EntitySettings } from 'common/models/internal/EntitySettings';
import type { TeamModelType } from 'common/models/flow/Team';
import {
    ENTITY_NAME_ID,
    ENTITY_EXPORT_ID,
    ENTITY_TABLE_ID
} from 'common/constants/entity/personnelMaster';
import {
    getLanguageName,
    getLanguageIdFilterOptions
} from 'common/constants/languageCodes';

import PersonnelMasterRoleFilter from './PersonnelMasterRoleFilter';
import PersonnelMasterTransporterFilter from './PersonnelMasterTransporterFilter';
import { isFieldHidden } from './utils';

import theme from 'common/entity/BulkEditContainer.scss';
import PlatformEntityExportHandler from 'common/entity/common/platform/PlatformEntityExportHandler';

type Props = {
    t: TFunction,
    entitySettings: EntitySettings,
    selectedRows: Array<string>,
    toggleRowSelection: string => void,
    theme: ThemeType,
    teams: Immutable.List<TeamModelType>,
    isGlobalPersonnel: boolean,
    isPersonnelBlockingEnabled: boolean,
    clientId: string,
    fetchOptions: Object,
    unblockPersonnel: ({
        clientId: string,
        personnelId: string
    }) => Promise<$FixMe>
};

const selectTeamId = ({ teamId }) => teamId;

const getTeamIdFilter = ({ clientId, filteredColumn }) => {
    const {
        value: { allowEmptyOrNull, values }
    } = filteredColumn;
    const teamValueFilter = values.map(team => ({
        clientId,
        teamId: team
    }));

    return new FilterBuilder()
        .withName('teams')
        .withOperation(FilterOperation.Contains)
        .withValues(teamValueFilter)
        .withAllowEmptyOrNull(allowEmptyOrNull);
};

const getBooleanFilterOptions = ({ column }) => {
    const { t } = column.getHeaderProps();
    const optionsForSelect = [
        { value: true, label: t('masterPersonnelTable.boolean.yes') },
        { value: false, label: t('masterPersonnelTable.boolean.no') }
    ];
    return optionsForSelect;
};

const DEFAULT_COLUMNS: Array<EntityColumnType> = [
    {
        id: 'selector',
        Cell: props => {
            const {
                tdProps: {
                    rest: { isChecked, onCheckboxClick }
                }
            } = props;
            return (
                <Checkbox
                    checkboxColor={dashboardColors.darkSkyBlue.name}
                    className={theme.selectionCell}
                    theme={theme}
                    checked={isChecked}
                    onChange={onCheckboxClick}
                />
            );
        },
        sortable: false,
        filterable: false,
        frozen: true,
        disablePinControl: true,
        width: 40
    },
    {
        headerTextId: 'masterPersonnelTable.id',
        accessor: 'id',
        minWidth: 100,
        infixSearchable: true,
        frozen: true
    },
    {
        headerTextId: 'masterPersonnelTable.code',
        accessor: 'code',
        frozen: true,
        infixSearchable: true,
        show: false
    },
    {
        headerTextId: 'masterPersonnelTable.name',
        accessor: 'name'
    },
    {
        headerTextId: 'masterPersonnelTable.customRole',
        accessor: 'customRole'
    },
    {
        headerTextId: 'masterPersonnelTable.language',
        accessor: 'language',
        FilterComponent: GeneralFilter,
        getFilterOptions: getLanguageIdFilterOptions
    },
    {
        headerTextId: 'masterPersonnelTable.languageName',
        accessor: 'languageName',
        sortable: false,
        filterable: false,
        Cell: props => {
            const {
                original: { language },
                tdProps: {
                    rest: { t }
                }
            } = props;
            return getLanguageName(language, t) || language;
        }
    },
    {
        headerTextId: 'masterPersonnelTable.phoneNumber',
        accessor: 'phone.phoneNumber'
    },
    {
        headerTextId: 'masterPersonnelTable.emailAddress',
        accessor: 'email.emailAddress'
    },
    {
        headerTextId: 'masterPersonnelTable.personnelType',
        id: 'personnelType',
        accessor: 'personnelType',
        exportId: 'personnelType',
        FilterComponent: FuelTypeFilter,
        filterable: true,
        sortable: true,
        minWidth: 200,
        isHidden: ({ isFieldHidden, entitySettings }) => {
            return isFieldHidden('personnelType', { entitySettings });
        },
        Cell: props => {
            const {
                original: { personnelType },
                tdProps: {
                    rest: { t, theme }
                }
            } = props;
            const cellText = t(
                `masterPersonnelProperties.personnelTypes.${personnelType}`
            );
            return (
                <div title={cellText} className={theme.cellText}>
                    {cellText}
                </div>
            );
        },
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;

            return new FilterBuilder()
                .withName('personnelType')
                .withOperation(FilterOperation.Equals)
                .withValues(values)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        getFilterOptions: () => {
            const options = PersonnelTypeEnum.getOptions({
                translationKey: 'masterPersonnelProperties.personnelTypes'
            });
            return options;
        }
    },
    {
        headerTextId: 'masterPersonnelTable.transporterId',
        id: 'transporters',
        accessor: 'transporters',
        exportId: 'transporters.id',
        sortable: false,
        minWidth: 200,
        isHidden: ({ isFieldHidden, entitySettings }) =>
            isFieldHidden('transporters', { entitySettings }),
        Cell: props => {
            const {
                original: { transporters }
            } = props;
            return transporters.size > 0
                ? transporters.get(0).transporterId
                : '';
        },
        FilterComponent: PersonnelMasterTransporterFilter,
        getCustomFilterInfo: ({ filteredColumn, clientId }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;

            return new FilterBuilder()
                .withName('transporters')
                .withOperation(FilterOperation.Contains)
                .withValues(
                    values.map(value => ({
                        clientId,
                        transporterId: value
                    }))
                )
                .withAllowEmptyOrNull(allowEmptyOrNull);
        }
    },
    {
        headerTextId: 'masterPersonnelTable.hasAllTeamsAccess',
        accessor: 'hasAllTeamsAccess',
        Cell: props => {
            const {
                original: { hasAllTeamsAccess },
                tdProps: {
                    rest: { t }
                }
            } = props;
            return hasAllTeamsAccess
                ? t('masterPersonnelTable.boolean.yes')
                : t('masterPersonnelTable.boolean.no');
        },
        FilterComponent: GeneralFilter,
        getFilterOptions: getBooleanFilterOptions
    },
    {
        headerTextId: 'masterPersonnelTable.teamName',
        id: 'teams.name',
        accessor: 'teams',
        exportId: 'teams.name',
        sortable: false,
        minWidth: 200,
        FilterComponent: ConnectedTeamSelectorFilter,
        getCustomFilterInfo: getTeamIdFilter,
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { theme, teams }
                }
            } = props;
            const teamIds = original.teams;
            return <TeamList teams={teams} teamIds={teamIds} theme={theme} />;
        }
    },
    {
        headerTextId: 'masterPersonnelTable.teamId',
        id: 'teams.id',
        accessor: 'teams',
        exportId: 'teams.id',
        sortable: false,
        minWidth: 200,
        FilterComponent: ConnectedTeamIdSelectorFilter,
        getCustomFilterInfo: getTeamIdFilter,
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { theme, teams }
                }
            } = props;
            const teamIds = original.teams;
            return (
                <TeamList
                    teams={teams}
                    teamIds={teamIds}
                    theme={theme}
                    selectTeamName={selectTeamId}
                />
            );
        }
    },
    {
        headerTextId: 'masterPersonnelTable.canManageEntities',
        accessor: 'canManageEntities',
        isHidden: ({ isFieldHidden, entitySettings }) =>
            isFieldHidden('canManageEntities', { entitySettings }),
        Cell: props => {
            const {
                original: { canManageEntities },
                tdProps: {
                    rest: { t }
                }
            } = props;
            return canManageEntities
                ? t('masterPersonnelTable.boolean.yes')
                : t('masterPersonnelTable.boolean.no');
        },
        FilterComponent: GeneralFilter,
        getFilterOptions: getBooleanFilterOptions
    },
    {
        headerTextId: 'masterPersonnelTable.canVerifyEntities',
        accessor: 'canVerifyEntities',
        isHidden: ({ isFieldHidden, entitySettings }) =>
            isFieldHidden('canVerifyEntities', { entitySettings }),
        Cell: props => {
            const {
                original: { canVerifyEntities },
                tdProps: {
                    rest: { t }
                }
            } = props;
            return canVerifyEntities
                ? t('masterPersonnelTable.boolean.yes')
                : t('masterPersonnelTable.boolean.no');
        },
        FilterComponent: GeneralFilter,
        getFilterOptions: getBooleanFilterOptions
    },
    {
        headerTextId: 'masterPersonnelTable.canManagePersonnel',
        accessor: 'canManagePersonnel',
        isHidden: ({ isFieldHidden, entitySettings }) =>
            isFieldHidden('canManagePersonnel', { entitySettings }),
        Cell: props => {
            const {
                original: { canManagePersonnel },
                tdProps: {
                    rest: { t }
                }
            } = props;
            return canManagePersonnel
                ? t('masterPersonnelTable.boolean.yes')
                : t('masterPersonnelTable.boolean.no');
        },
        FilterComponent: GeneralFilter,
        getFilterOptions: getBooleanFilterOptions
    },
    {
        headerTextId: 'masterPersonnelTable.accessType',
        accessor: 'accessType',
        FilterComponent: FuelTypeFilter,
        sortable: false,
        filterable: false,
        isHidden: ({ isFieldHidden, entitySettings }) =>
            isFieldHidden('accessType', { entitySettings }),
        Cell: props => {
            const {
                original: { accessType },
                tdProps: {
                    rest: { t, theme }
                }
            } = props;
            const cellText = t(
                `masterPersonnelProperties.accessTypes.${accessType}`
            );
            return (
                <div title={cellText} className={theme.cellText}>
                    {cellText}
                </div>
            );
        },
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('accessType')
                .withOperation(FilterOperation.Equals)
                .withValues(values)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        getFilterOptions: () => {
            const options = AccessTypeEnum.getOptions({
                translationKey: 'masterPersonnelProperties.accessTypes'
            });
            return options;
        }
    },
    {
        headerTextId: 'masterPersonnelTable.roleMasterName',
        id: 'roleMaster.name',
        accessor: 'roleMaster.name',
        sortable: false,
        minWidth: 200,
        FilterComponent: PersonnelMasterRoleFilter,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;

            return new FilterBuilder()
                .withName('roleMasterId.id')
                .withOperation(FilterOperation.Equals)
                .withValues(values)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        isHidden: ({ isFieldHidden, entitySettings }) =>
            isFieldHidden('roleMasterName', { entitySettings })
    },
    {
        headerTextId: 'masterPersonnelTable.roleMasterId',
        id: 'roleMasterId.id',
        accessor: 'roleMasterId.id',
        sortable: true,
        filterable: false,
        isHidden: ({ isFieldHidden, entitySettings }) =>
            isFieldHidden('roleMasterId', { entitySettings })
    },
    {
        headerTextId: 'masterPersonnelTable.timezone',
        accessor: 'timezone',
        sortable: false,
        filterable: false,
        isHidden: ({ localeSettings }) =>
            !localeSettings.multiTimeZoneSupportEnabled
    },
    {
        headerTextId: 'masterPersonnelTable.updatedBy',
        id: 'updatedBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.updatedBy',
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const updatedBy = VisitUtils.formatIDs(
                original?.auditMetadata?.updatedBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={updatedBy} className={theme.cellText}>
                    {updatedBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'masterPersonnelTable.updatedOn',
        accessor: 'auditMetadata.updatedOn',
        id: 'updatedOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    },
    {
        headerTextId: 'masterPersonnelTable.createdBy',
        id: 'createdBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.createdBy',
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const createdBy = VisitUtils.formatIDs(
                original?.auditMetadata?.createdBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={createdBy} className={theme.cellText}>
                    {createdBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'masterPersonnelTable.createdOn',
        accessor: 'auditMetadata.createdOn',
        id: 'createdOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    }
];

const PersonnelMaster = entityMasterFactory({
    EntityEditViewContainer: SinglePersonnelEditContainer,
    EntityImportContainer: PersonnelMasterImportContainer,
    EntityExportContainer: MasterExportContainer,
    COLUMNS: DEFAULT_COLUMNS,
    EXPORT_HANDLER: new PlatformEntityExportHandler(),
    options: {
        entityTableId: ENTITY_TABLE_ID,
        operations: {
            add: RoleOperationEnum.UPDATE_PERSONNELS,
            edit: RoleOperationEnum.UPDATE_PERSONNELS,
            export: RoleOperationEnum.EXPORT_PERSONNELS
        }
    },
    EnableEntityView: EnablePersonnelModal
});

const PersonnelMasterContainer = (props: Props): React.Node => {
    const [
        isUnblockConfirmationDialogOpen,
        setIsUnblockConfirmationDialogOpen
    ] = React.useState(false);
    const [isUnblockInProgress, setIsUnblockInProgress] = React.useState(false);

    const getTdProps = (_, rowInfo) => {
        if (!rowInfo) {
            return {};
        }
        const { id } = rowInfo.original;
        const { selectedRows, toggleRowSelection, theme } = props;
        const isChecked = selectedRows.indexOf(id) >= 0;
        return {
            onCheckboxClick: () => {
                toggleRowSelection(id);
            },
            t: props.t,
            entitySettings: props.entitySettings,
            theme: props.theme,
            isChecked,
            teams: props.teams,
            isGlobalPersonnel: props.isGlobalPersonnel,
            className: classnames({ [theme.selectedRow]: isChecked })
        };
    };

    const handleUnblock = () => {
        setIsUnblockConfirmationDialogOpen(true);
    };

    const handleUnblockAccept = () => {
        const { selectedRows, clientId, unblockPersonnel } = props;
        const [personnelId] = selectedRows;
        setIsUnblockInProgress(true);
        unblockPersonnel({ personnelId, clientId }).finally(() => {
            setIsUnblockInProgress(false);
            setIsUnblockConfirmationDialogOpen(false);
        });
    };

    const handleUnblockDismiss = () => {
        setIsUnblockConfirmationDialogOpen(false);
    };

    const {
        clientId,
        fetchOptions,
        selectedRows,
        t,
        isPersonnelBlockingEnabled
    } = props;

    const personnelMasterFetchOptions = {
        clientId,
        include: [IncludeTypeEnums.IncludeAuth, IncludeTypeEnums.IncludeRole],
        ...(fetchOptions || {})
    };

    const customActions = {
        onUnblockPersonnel:
            isPersonnelBlockingEnabled && selectedRows.length === 1
                ? handleUnblock
                : null
    };

    return (
        <PersonnelMaster
            {...props}
            customActions={customActions}
            emptyStateImageSrc="/img/masterPersonnel-empty.svg"
            entityNameId={ENTITY_NAME_ID}
            entityExportId={ENTITY_EXPORT_ID}
            entityId="personnelId"
            entitySettingsName="personnelSettings"
            fetchOptions={personnelMasterFetchOptions}
            getTdProps={getTdProps}
            hasFreezableColumns
            frozenColumnLimit={FROZEN_COLUMN_LIMIT}
            pinTooltip={t('pinTooltip')}
            unpinTooltip={t('unpinTooltip')}
            entityCanBeDisabled
            enableResetPassword={selectedRows.length === 1}
            isFieldHidden={isFieldHidden}
        >
            {isUnblockConfirmationDialogOpen ? (
                <ConfirmationDialog
                    isLoading={isUnblockInProgress}
                    theme={theme}
                    onAccept={handleUnblockAccept}
                    onCancel={handleUnblockDismiss}
                    onDismiss={handleUnblockDismiss}
                    acceptActionLabel={t('yesUnblock')}
                    active
                    title={t('unblockPersonnelConfirmationTitle')}
                    innerText={t('unblockPersonnelConfirmationMessage')}
                />
            ) : null}
        </PersonnelMaster>
    );
};

export default compose(
    themr('PersonnelMasterContainer', theme),
    translate('entityManagement'),
    withRouter,
    connect(
        (state, ownProps) => {
            return {
                clientId: clientIdSelector(state, ownProps),
                isLoading: PersonnelMasterSelectors.isEntitiesLoading(
                    state,
                    ownProps
                ),
                data: PersonnelMasterSelectors.getEntities(state, ownProps),
                isAddEntityActive: isAddEntityActiveSelector(state, ownProps),
                entityIdToEdit: editEntityIdSelector(state, ownProps),
                entitySettings: getEntitySettings(state, ownProps),
                tableSettings: getEntityTablePreferences(
                    state,
                    ENTITY_TABLE_ID
                ),
                isImportEntityActive: isImportEntityActiveSelector(
                    state,
                    ownProps
                ),
                isExportEntityActive: isExportEntityActiveSelector(
                    state,
                    ownProps
                ),
                isGlobalPersonnel: isGlobalPersonnelSelector(state, ownProps),
                isPersonnelBlockingEnabled: clientSelector(
                    state,
                    ownProps
                )?.isPersonnelBlockingEnabled(),
                teams: teamsSelector(state, ownProps)
            };
        },
        {
            fetchEntities: PersonnelActions.fetchActiveEntities,
            showToastAction: ToastActions.showToast,
            updateToastAction: ToastActions.updateToast,
            hideToastAction: ToastActions.hideToast,
            disableEntity: PersonnelActions.disableEntity,
            clearData: PersonnelActions.clearData,
            saveTableSettings: AppPreferencesActions.saveEntityTablePreferences,
            bulkDisableEntity: PersonnelActions.bulkDisableEntity,
            resetPassword: PersonnelActions.resetPassword,
            unblockPersonnel: PersonnelActions.unblock
        }
    ),
    MasterContainerHOC
)(PersonnelMasterContainer);
